import { forwardRef } from 'react';
import type { AutosaveState } from '@components/AutosaveStatus';
import { PersonOffOutlined, PersonOutlined } from '@mui/icons-material';
import { CircularProgress, MenuItem } from '@mui/material';

export interface PatientActivityControlProps {
  /** Last confirmed status; an error must retain the same requested transition. */
  active: boolean;
  canEdit: boolean;
  saveState?: AutosaveState;
  onChange: (active: boolean) => void;
  tabIndex?: number;
}

/** A reversible status action in the patient menu. */
export const PatientActivityControl = forwardRef<HTMLLIElement, PatientActivityControlProps>(
  function PatientActivityControl(
    { active, canEdit, saveState = 'idle', onChange, tabIndex },
    ref,
  ) {
    if (!canEdit) return null;
    const saving = saveState === 'saving';
    return (
      <MenuItem
        ref={ref}
        tabIndex={tabIndex}
        disabled={saving}
        aria-busy={saving}
        onClick={() => {
          if (!saving) onChange(!active);
        }}
      >
        {saving ? (
          <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} aria-label="Saving status" />
        ) : active ? (
          <PersonOffOutlined fontSize="small" sx={{ mr: 1 }} />
        ) : (
          <PersonOutlined fontSize="small" sx={{ mr: 1 }} />
        )}
        {saving ? 'Saving…' : active ? 'Mark inactive' : 'Mark active'}
      </MenuItem>
    );
  },
);
